/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import React from 'react';
import { Button, TextField, Table, TableBody, TableCell, TableHead, TableRow, InputAdornment } from '@material-ui/core';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import shortid from 'shortid';
var utils = require('../../../Utils.js');

/*
 *  CorrectionRange is a component used for gathering the correction range information based off of the clinician inputs.
 *  It is a dynamically changing table with guardrails and warnings. Delete row functionality is included.
 */

class CorrectionRange extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
    // Allows for values to be passed in, or will start from scratch if no values provided
    this.state.values = props.values
      ? props.values
      : [
          {
            id: shortid.generate(),
            time: '00:00',
            lowerLimit: 110,
            upperLimit: 120
            // isFirst: true
          }
        ];
    this.props.setFieldValue(this.props.fieldType, this.state.values);
  }

  // handles row deletion
  handleRowDel(value) {
    var index = this.state.values.indexOf(value);
    this.state.values.splice(index, 1);
    this.setState(this.state.values.sort(utils.compare));
    // function below updates the values in the formik state handler
    this.props.setFieldValue(this.props.fieldType, this.state.values);
  }

  // handles row addition
  handleAddEvent(evt) {
    var nextTime = this.state.values.reduce((max, p) => (p.time > max ? p.time : max), this.state.values[0].time);
    var nextMinutes = (parseInt(nextTime.slice(3, 5)) + 30) % 60;
    var nextHour =
      parseInt(nextTime.slice(3, 5)) + 30 >= 60 ? parseInt(nextTime.slice(0, 2)) + 1 : parseInt(nextTime.slice(0, 2));
    nextHour = ('0' + nextHour).slice(-2);
    nextMinutes = ('0' + nextMinutes).slice(-2);
    var nextValue = utils.getLastValue(this.state.values, 'cRange');

    var value = {
      id: shortid.generate(),
      time: nextHour + ':' + nextMinutes,
      lowerLimit: nextValue.lowerLimit,
      upperLimit: nextValue.upperLimit
      // isFirst: false
    };

    this.state.values.push(value);
    this.setState(this.state.values.sort(utils.compare));
    // function below updates the values in the formik state handler
    this.props.setFieldValue(this.props.fieldType, this.state.values);
  }

  // For any change that happens in the table (ie: a value increases) it is handled here
  handleProductTable(evt) {
    var item = {
      id: evt.target.id,
      name: evt.target.name,
      value: evt.target.value
    };
    var values = this.state.values.slice();
    var newValues = values.map(function(value) {
      for (var key in value) {
        if (key === item.name && value.id === item.id) {
          value[key] = item.value;
        }
      }
      return value;
    });
    this.setState({ values: newValues.sort(utils.compare) });
    // function below updates the values in the formik state handler
    this.props.setFieldValue(this.props.fieldType, this.state.values);
  }

  render() {
    return (
      <ProductTable
        onProductTableUpdate={this.handleProductTable.bind(this)}
        onRowAdd={this.handleAddEvent.bind(this)}
        onRowDel={this.handleRowDel.bind(this)}
        products={this.state.values}
        tableType={this.props.tableType}
        units={this.props.units}
        addAnAdditionalText={this.props.addAnAdditionalText}
        errors={this.props.errors}
        touched={this.props.tocuhed}
      />
    );
  }
}

// Helper component for the Correction Range, this is the format of the table itself
class ProductTable extends React.Component {
  render() {
    var onProductTableUpdate = this.props.onProductTableUpdate;
    var rowDel = this.props.onRowDel;
    var len = this.props.products.length;
    var tableType = this.props.tableType;
    var units = this.props.units;
    var errors = this.props.errors;
    var touched = this.props.touched;
    var loopValues = this.props.products.map((loopValue, index) => {
      return (
        <ProductRow
          onProductTableUpdate={onProductTableUpdate}
          product={loopValue}
          length={len}
          onDelEvent={rowDel.bind(this)}
          key={loopValue.id}
          tableType={tableType}
          units={units}
          errors={errors}
          touched={touched}
          index={index}
        />
      );
    });

    return (
      <div style={{ width: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left">Start Time</TableCell>
              <TableCell align="left">Lower Target</TableCell>
              <TableCell padding="checkbox">-</TableCell>
              <TableCell align="left">Upper Target</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{loopValues}</TableBody>
        </Table>
        <Button
          onClick={this.props.onRowAdd}
          fullWidth
          color="primary"
          variant="text"
          style={{ textTransform: 'initial' }}
        >
          Add an additional correction range
        </Button>
      </div>
    );
  }
}

// Helper component, is an individual row in a table
class ProductRow extends React.Component {
  // calls on delete handler
  onDelEvent() {
    this.props.onDelEvent(this.props.product);
  }

  render() {
    return (
      <TableRow>
        <TimeCell
          onProductTableUpdate={this.props.onProductTableUpdate}
          cellData={{
            type: 'time',
            value: this.props.product.time,
            id: this.props.product.id,
            isFirst: this.props.product.isFirst
          }}
          errors={this.props.errors}
          touched={this.props.touched}
        />
        <ValueCell
          onProductTableUpdate={this.props.onProductTableUpdate}
          cellData={{
            type: 'lowerLimit',
            value: this.props.product.lowerLimit,
            id: this.props.product.id
          }}
          tableType={this.props.tableType}
          units={this.props.units}
          errors={this.props.errors}
          touched={this.props.touched}
        />
        <TableCell padding="checkbox">-</TableCell>
        <ValueCell
          onProductTableUpdate={this.props.onProductTableUpdate}
          cellData={{
            type: 'upperLimit',
            value: this.props.product.upperLimit,
            id: this.props.product.id
          }}
          tableType={this.props.tableType}
          units={this.props.units}
          errors={this.props.errors}
          touched={this.props.touched}
        />
        <TableCell align="right" padding="checkbox">
          {this.props.index === 0 ? (
            <Button disabled>
              <DeleteOutline color="disabled" />
            </Button>
          ) : (
            <Button onClick={this.onDelEvent.bind(this)}>
              <DeleteOutline color="primary" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }
}

// Helper component, is the time picker for the correction range
class TimeCell extends React.Component {
  render() {
    return (
      <TableCell align="left">
        <TextField
          fullWidth
          variant="outlined"
          type="time"
          InputProps={{
            readOnly: this.props.cellData.isFirst
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{ step: 1800 }}
          //style={{width: 135}}
          name={this.props.cellData.type}
          id={this.props.cellData.id}
          value={this.props.cellData.value}
          onChange={this.props.onProductTableUpdate}
          error={this.props.touched && Boolean(this.props.errors)}
          helperText={this.props.touched ? this.props.errors : ''}
        />
      </TableCell>
    );
  }
}

// Helper component, is the range selector for the correction range
class ValueCell extends React.Component {
  render() {
    return (
      <TableCell align="center">
        <TextField
          fullWidth
          variant="outlined"
          type="number"
          // style={{width: 120}}
          name={this.props.cellData.type}
          id={this.props.cellData.id}
          value={this.props.cellData.value}
          onChange={this.props.onProductTableUpdate}
          InputProps={{
            endAdornment: <InputAdornment position="end">mg/dL</InputAdornment>,
            style: { color: utils.correctionRangeWarnings(this.props.cellData.value).color }
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={utils.barriers('cRange')}
          FormHelperTextProps={{
            style: { color: utils.correctionRangeWarnings(this.props.cellData.value).color }
          }}
          error={this.props.touched && Boolean(this.props.errors)}
          helperText={utils.helperTextPicker(
            this.props.touched,
            this.props.errors,
            utils.correctionRangeWarnings(this.props.cellData.value)
          )}
        />
      </TableCell>
    );
  }
}

export default CorrectionRange;
